import { createContext, useCallback, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "demj_cart_v1";

const CartContext = createContext({
  items: [],
  isOpen: false,
  openCart: () => {},
  closeCart: () => {},
  addItem: () => {},
  removeItem: () => {},
  updateQuantity: () => {},
  clear: () => {},
  totalGbp: 0,
  totalQuantity: 0,
});

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore quota errors
    }
  }, [items]);

  const addItem = useCallback((item) => {
    if (!item || !item.id) return;
    setItems((prev) => {
      const existing = prev.find((p) => p.id === item.id);
      if (existing) {
        return prev.map((p) =>
          p.id === item.id ? { ...p, quantity: (p.quantity || 1) + (item.quantity || 1) } : p
        );
      }
      return [
        ...prev,
        {
          id: item.id,
          name: item.name || "Item",
          category: item.category || "",
          price_gbp_string: item.price_gbp_string || "",
          unit_amount_gbp: Number(item.unit_amount_gbp) || 0,
          image: item.image || "",
          quantity: Math.max(1, Number(item.quantity) || 1),
        },
      ];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const updateQuantity = useCallback((id, qty) => {
    const n = Math.max(1, Math.min(99, Number(qty) || 1));
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, quantity: n } : p)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totalGbp = items.reduce((s, i) => s + (i.unit_amount_gbp || 0) * (i.quantity || 1), 0);
  const totalQuantity = items.reduce((s, i) => s + (i.quantity || 1), 0);

  return (
    <CartContext.Provider
      value={{
        items,
        isOpen,
        openCart: () => setIsOpen(true),
        closeCart: () => setIsOpen(false),
        addItem,
        removeItem,
        updateQuantity,
        clear,
        totalGbp,
        totalQuantity,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
